import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../api";
import Layout from "../components/Layout";

export default function EvasionStudentsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialClassId = searchParams.get("classId") || "";
  const initialQuery = searchParams.get("q") || "";

  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [classFilter, setClassFilter] = useState(initialClassId);
  const [searchInput, setSearchInput] = useState(initialQuery);
  const [query, setQuery] = useState(initialQuery);

  const fetchClasses = useCallback(async () => {
    const response = await api.get("/classes");
    const list = Array.isArray(response.data) ? response.data : response.data?.data || [];
    setClasses(list);
  }, []);

  const fetchEvaded = useCallback(async (pageNumber = 1, classId = classFilter, q = query) => {
    const params = new URLSearchParams();
    params.set("page", String(pageNumber));
    params.set("limit", "10");

    if (classId) {
      params.set("classId", String(classId));
    }

    if (q) {
      params.set("q", String(q));
    }

    const response = await api.get(`/dashboard/evaded-students?${params.toString()}`);

    setStudents(response.data?.data || []);
    setPage(response.data?.page || 1);
    setTotalPages(response.data?.totalPages || 1);
  }, [classFilter, query]);

  useEffect(() => {
    fetchClasses();
  }, [fetchClasses]);

  useEffect(() => {
    fetchEvaded(1, initialClassId, initialQuery);
  }, [fetchEvaded, initialClassId, initialQuery]);

  function applyFilters() {
    const nextQuery = searchInput.trim();
    setQuery(nextQuery);
    setSearchParams({
      ...(classFilter ? { classId: String(classFilter) } : {}),
      ...(nextQuery ? { q: nextQuery } : {}),
    });
    fetchEvaded(1, classFilter, nextQuery);
  }

  function clearFilters() {
    setClassFilter("");
    setSearchInput("");
    setQuery("");
    setSearchParams({});
    fetchEvaded(1, "", "");
  }

  return (
    <Layout>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4>Alunos Evadidos</h4>
        <button className="btn btn-outline-secondary" onClick={() => navigate("/analytics")}>
          Voltar ao Analytics
        </button>
      </div>

      <div className="card shadow-sm p-3 mb-3">
        <div className="row g-2">
          <div className="col-md-3">
            <select
              className="form-select"
              value={classFilter}
              onChange={(e) => setClassFilter(e.target.value)}
            >
              <option value="">Todas as turmas</option>
              {classes.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-5">
            <input
              className="form-control"
              placeholder="Buscar por nome ou matricula"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") applyFilters();
              }}
            />
          </div>

          <div className="col-md-4 d-flex gap-2">
            <button className="btn btn-outline-primary" onClick={applyFilters}>
              Filtrar
            </button>
            <button className="btn btn-outline-secondary" onClick={clearFilters}>
              Limpar
            </button>
          </div>
        </div>
      </div>

      <div className="card shadow-sm p-3">
        <div className="table-responsive">
          <table className="table table-sm align-middle">
            <thead>
              <tr>
                <th>Aluno</th>
                <th>Matricula</th>
                <th>Turma</th>
                <th>Confirmado em</th>
                <th>Acoes</th>
              </tr>
            </thead>
            <tbody>
              {students.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-muted">
                    Nenhum aluno evadido encontrado.
                  </td>
                </tr>
              ) : (
                students.map((student) => (
                  <tr key={student.id}>
                    <td>{student.name}</td>
                    <td>{student.registrationNumber}</td>
                    <td>{student.class?.name || "-"}</td>
                    <td>
                      {student.followUps?.[0]?.closedAt
                        ? new Date(student.followUps[0].closedAt).toLocaleDateString()
                        : "-"}
                    </td>
                    <td>
                      <button
                        className="btn btn-sm btn-outline-dark"
                        onClick={() => navigate(`/students/${student.id}/details`)}
                      >
                        Detalhes
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="d-flex justify-content-center align-items-center mt-2">
          <button
            className="btn btn-outline-secondary me-3"
            disabled={page <= 1}
            onClick={() => fetchEvaded(page - 1, classFilter, query)}
          >
            Anterior
          </button>

          <span>
            Pagina {page} de {totalPages}
          </span>

          <button
            className="btn btn-outline-secondary ms-3"
            disabled={page >= totalPages}
            onClick={() => fetchEvaded(page + 1, classFilter, query)}
          >
            Proxima
          </button>
        </div>
      </div>
    </Layout>
  );
}
